import React from 'react';

import { SearchInput } from '@neptune/shared/common-ui';
import { useAsyncValue } from '@neptune/shared/common-util';
import { bemBlock, LayoutColumn } from '@neptune/shared/venus-ui';

import { useDebounce } from 'common/hooks/useDebounce';

import { FilterSearchItem } from './FilterSearchItem';
import { FilterSearchResults } from './FilterSearchResults';
import { FilterSearchResultsText } from './FilterSearchResultsText';
import { FilterSearchLayoutProps, FilterSearchProps } from './interfaces';
import { RegExpTooltip } from './RegExpTooltip';

import './FilterSearch.less';

const block = bemBlock('filter-search');

const FilterSearchPlainLayout: React.FC<Omit<FilterSearchLayoutProps, 'query'>> = ({
  input,
  message,
  searchResults,
}) => (
  <>
    {input}
    {message}
    {searchResults}
  </>
);

export const FilterSearch = <T,>({
  className,
  dataSource,
  'data-role': dataRole,
  debounceTimeout,
  initialValue,
  inputRef,
  RenderItem = FilterSearchItem,
  onKeyDown,
  onKeyUp,
  onSelect,
  span,
  placeholder = '',
  showCounters = true,
  size = 'medium',
  withIcon,
  renderLayout,
  HelpInfo,
  regexSupport = false,
}: FilterSearchProps<T>) => {
  const [query, setQuery] = React.useState('');

  const debouncedQuery = useDebounce(query, 256);

  const resolver = React.useCallback(
    () => dataSource(debouncedQuery),
    [dataSource, debouncedQuery],
  );

  const { value, error, loading } = useAsyncValue({ resolver });

  const results = value?.entries ?? [];

  React.useEffect(() => {
    setQuery(initialValue || '');
  }, [initialValue]);

  const input = (
    <SearchInput
      value={query}
      onChange={setQuery}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      debounceTimeout={dataSource.isStatic ? 0 : debounceTimeout}
      inputRef={inputRef}
      size={size}
      placeholder={placeholder}
      className={block('input')}
      HelpInfo={HelpInfo}
      autoFocus
      withIcon={withIcon}
      spacedChildren="xs"
      suffixElement={regexSupport ? <RegExpTooltip /> : undefined}
    />
  );

  const message = (
    <FilterSearchResultsText
      emptyQuery={query.length === 0}
      currentCount={results.length}
      error={error}
      loading={loading}
      showCounters={showCounters}
      thereIsMore={value?.thereIsMore}
    />
  );

  const searchResults = (
    <FilterSearchResults
      query={query}
      results={results}
      loading={loading}
      error={error}
      onSelect={onSelect}
      RenderItem={RenderItem}
      className={block('results')}
    />
  );

  return (
    <LayoutColumn
      className={block({ extra: className })}
      data-role={dataRole}
      spacedChildren="sm"
      span={span}
    >
      {renderLayout === undefined ? (
        <FilterSearchPlainLayout input={input} message={message} searchResults={searchResults} />
      ) : (
        renderLayout({ input, message, searchResults, query })
      )}
    </LayoutColumn>
  );
};
